import { BaseCryptoUtils } from './crypto/BaseCryptoUtils';
export var StorageKeys;
(function (StorageKeys) {
    StorageKeys["AuthToken"] = "auth_token";
    StorageKeys["AuthRefreshToken"] = "auth_refresh_token";
    StorageKeys["UserProfile"] = "ibk_user_profile";
    StorageKeys["UserPreferences"] = "ibk_user_preferences";
    StorageKeys["DeviceId"] = "deviceId";
    StorageKeys["DeviceValidated"] = "ibk_device_validated";
    StorageKeys["AccountId"] = "account_id";
    StorageKeys["AllAccounts"] = "all_accounts";
    StorageKeys["LastAccessedCompany"] = "last_accessed_company";
    StorageKeys["BillingProduct"] = "billing_product";
    StorageKeys["BillingProductNFSe"] = "billing_product_nfse";
    StorageKeys["BillingProductRegua"] = "billing_product_regua";
    StorageKeys["DeepLinkData"] = "ibk_deeplink_data";
    StorageKeys["PendingPaymentsList"] = "pending_payments_list";
    StorageKeys["PendingPaymentsTotal"] = "pending_payments_total";
    StorageKeys["HappyHolidaysEndOfTheYearRechargeModalAlreadySeen"] = "happy_holidays_end_of_year_recharge_modal_already_seen";
    StorageKeys["TermsAcceptanceDailyCache"] = "ibk_terms_acceptance_daily_cache";
    StorageKeys["CompanyId"] = "company_id";
    StorageKeys["ClientId"] = "client_id";
    StorageKeys["EnabledFeature"] = "ibk_enabled_feature";
})(StorageKeys || (StorageKeys = {}));
export class StorageService extends BaseCryptoUtils {
    static CRYPTO_VERSION = 'v2';
    storage;
    constructor(storage) {
        super();
        this.storage = storage;
    }
    async getOrCreateCryptoKey() {
        const storedKeyData = window.localStorage.getItem(BaseCryptoUtils.CRYPTO_KEY_STORAGE);
        if (storedKeyData) {
            try {
                const keyData = JSON.parse(storedKeyData);
                return await window.crypto.subtle.importKey('raw', new Uint8Array(keyData), 'AES-GCM', false, [
                    'encrypt',
                    'decrypt',
                ]);
            }
            catch (error) {
                console.warn('Failed to import existing crypto key, generating new one');
            }
        }
        const key = await window.crypto.subtle.generateKey({
            name: 'AES-GCM',
            length: BaseCryptoUtils.AES_KEY_LENGTH,
        }, true, ['encrypt', 'decrypt']);
        try {
            const exportedKey = await window.crypto.subtle.exportKey('raw', key);
            const keyArray = Array.from(new Uint8Array(exportedKey));
            window.localStorage.setItem(BaseCryptoUtils.CRYPTO_KEY_STORAGE, JSON.stringify(keyArray));
        }
        catch (error) {
            console.warn('Failed to store crypto key, will regenerate on next session');
        }
        return key;
    }
    async encryptData(text) {
        try {
            if (!window.crypto?.subtle) {
                throw new Error('Web Crypto API not available');
            }
            const key = await this.getOrCreateCryptoKey();
            const encoder = new TextEncoder();
            const data = encoder.encode(text);
            const iv = window.crypto.getRandomValues(new Uint8Array(BaseCryptoUtils.IV_LENGTH));
            const encryptedData = await window.crypto.subtle.encrypt({
                name: 'AES-GCM',
                iv: iv,
            }, key, data);
            const combined = new Uint8Array(iv.length + encryptedData.byteLength + 2);
            combined.set(iv, 0);
            combined.set(new Uint8Array(encryptedData), iv.length);
            const versionBytes = new TextEncoder().encode(StorageService.CRYPTO_VERSION);
            combined.set(versionBytes, iv.length + encryptedData.byteLength);
            return btoa(String.fromCharCode(...combined));
        }
        catch (error) {
            console.warn('Crypto API encryption failed, falling back to simple encryption');
            return this.simpleEncrypt(text, this.getObfuscatedKey());
        }
    }
    async decryptData(encryptedText) {
        try {
            const combined = new Uint8Array(atob(encryptedText)
                .split('')
                .map(char => char.charCodeAt(0)));
            const versionBytes = combined.slice(-2);
            const version = new TextDecoder().decode(versionBytes);
            if (version === StorageService.CRYPTO_VERSION && window.crypto?.subtle) {
                const key = await this.getOrCreateCryptoKey();
                const iv = combined.slice(0, BaseCryptoUtils.IV_LENGTH);
                const encryptedData = combined.slice(BaseCryptoUtils.IV_LENGTH, -2);
                const decryptedData = await window.crypto.subtle.decrypt({
                    name: 'AES-GCM',
                    iv: iv,
                }, key, encryptedData);
                return new TextDecoder().decode(decryptedData);
            }
            else {
                return this.simpleDecrypt(encryptedText, this.getObfuscatedKey());
            }
        }
        catch (error) {
            try {
                return this.simpleDecrypt(encryptedText, this.getObfuscatedKey());
            }
            catch (fallbackError) {
                throw new Error('Failed to decrypt data with both methods');
            }
        }
    }
    handleStorageError(operation, key, error) {
        const keyContext = key ? `data '${key}'` : 'data';
        let preposition = ' in';
        if (operation.includes('from')) {
            preposition = ' from';
        }
        else if (operation.includes('to')) {
            preposition = ' to';
        }
        const context = `${keyContext}${preposition} storage`;
        this.handleError(operation, context, error);
    }
    async processData(key, value, isSet) {
        try {
            if (isSet && value !== null) {
                const serializedData = JSON.stringify(value);
                const encryptedValue = await this.encryptData(serializedData);
                this.storage.setItem(key, encryptedValue);
            }
            else {
                const encryptedItem = this.storage.getItem(key);
                if (!encryptedItem)
                    return null;
                try {
                    const decryptedValue = await this.decryptData(encryptedItem);
                    return JSON.parse(decryptedValue);
                }
                catch (error) {
                    console.warn(`Failed to decrypt storage item '${key}'. The data may be corrupted or from a different session.`);
                    return null;
                }
            }
        }
        catch (error) {
            this.handleStorageError(isSet ? 'save' : 'retrieve', key, error);
        }
    }
    processDataSync(key, value, isSet) {
        try {
            if (isSet && value !== null) {
                const serializedData = JSON.stringify(value);
                const encryptedValue = this.simpleEncrypt(serializedData, this.getObfuscatedKey());
                this.storage.setItem(key, encryptedValue);
            }
            else {
                const encryptedItem = this.storage.getItem(key);
                if (!encryptedItem)
                    return null;
                try {
                    const decryptedValue = this.simpleDecrypt(encryptedItem, this.getObfuscatedKey());
                    return JSON.parse(decryptedValue);
                }
                catch (error) {
                    console.warn(`Failed to decrypt storage item '${key}'. The data may be corrupted or from a different session.`);
                    return null;
                }
            }
        }
        catch (error) {
            this.handleStorageError(isSet ? 'save' : 'retrieve', key, error);
        }
    }
    async setItem(key, value) {
        await this.processData(key, value, true);
    }
    async getItem(key) {
        return (await this.processData(key, null, false));
    }
    removeItem(key) {
        try {
            this.storage.removeItem(key);
        }
        catch (error) {
            this.handleStorageError('remove', key, error);
        }
    }
    setItemSync(key, value) {
        this.processDataSync(key, value, true);
    }
    getItemSync(key) {
        return this.processDataSync(key, null, false);
    }
    clearAllKeys() {
        this.clearCryptoKeys();
    }
    hasCryptoKeys() {
        try {
            return !!window.localStorage.getItem(BaseCryptoUtils.CRYPTO_KEY_STORAGE);
        }
        catch (error) {
            return false;
        }
    }
    clear() {
        this.storage.clear?.();
    }
}
export const localStorageService = new StorageService(window.localStorage);
export const sessionStorageService = new StorageService(window.sessionStorage);
